import * as React from "react";
import {
  ArrowUpRight,
  BriefcaseBusiness,
  Circle,
  Code2,
  GraduationCap,
  Megaphone,
  Scale,
  TrendingUp,
  Truck,
  Users,
  Utensils,
  type LucideIcon,
} from "lucide-react";

import type { FunctionalAreaFacet } from "@/lib/job-board/types";
import {
  functionalAreaJobsUrl,
  groupFunctionalAreaFacets,
  jobCountLabel,
  type JobCategory,
} from "@/lib/job-board/functional-area-utils";
import { Button } from "@/components/ui/button";
import JobCategoriesSkeleton from "./job-categories-skeleton";

const INITIAL_CATEGORY_COUNT = 6;

const categoryIcons: Record<string, LucideIcon> = {
  "education & training": GraduationCap,
  sales: TrendingUp,
  "operations & supply chain": Truck,
  technology: Code2,
  business: BriefcaseBusiness,
  "hr & people ops": Users,
  marketing: Megaphone,
  "finance & legal": Scale,
  hospitality: Utensils,
};

function iconFor(category: JobCategory): LucideIcon {
  return categoryIcons[category.label.trim().replace(/\s+/g, " ").toLowerCase()] || Circle;
}

type Props = {
  initialFacets?: FunctionalAreaFacet[];
  initialError?: boolean;
};

export default function JobCategoriesSection({ initialFacets, initialError = false }: Props) {
  const [facets, setFacets] = React.useState<FunctionalAreaFacet[]>(initialFacets || []);
  const [isLoading, setIsLoading] = React.useState(!initialFacets && !initialError);
  const [hasError, setHasError] = React.useState(initialError);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const categories = React.useMemo(() => groupFunctionalAreaFacets(facets), [facets]);
  const visibleCategories = isExpanded ? categories : categories.slice(0, INITIAL_CATEGORY_COUNT);

  const loadFacets = React.useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await fetch("/api/facets/functional-areas");
      if (!response.ok) throw new Error("Unable to load functional areas");
      const data: unknown = await response.json();
      setFacets(Array.isArray(data) ? (data as FunctionalAreaFacet[]) : []);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!initialFacets && !initialError) void loadFacets();
  }, [initialError, initialFacets, loadFacets]);

  return (
    <section className="container py-10" aria-labelledby="job-categories-heading" aria-busy={isLoading || undefined}>
      <div className="mb-6 max-w-2xl">
        <p className="text-eyebrow mb-2">Explore opportunities</p>
        <h2 id="job-categories-heading" className="mb-2">
          Explore Jobs by Category
        </h2>
        <p className="m-0 text-sm text-muted-foreground sm:text-[15px]">
          Discover opportunities across industries, roles, and specializations.
        </p>
      </div>

      {isLoading ? <JobCategoriesSkeleton /> : null}

      {!isLoading && hasError ? (
        <div className="rounded-xl border border-border bg-card px-5 py-8 text-center" role="status" aria-live="polite">
          <p className="m-0 text-sm text-muted-foreground">We couldn’t load job categories right now.</p>
          <Button type="button" variant="outline" size="sm" className="mt-4" onClick={loadFacets}>
            Try again
          </Button>
        </div>
      ) : null}

      {!isLoading && !hasError && categories.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-5 py-8 text-center">
          <p className="m-0 text-sm text-muted-foreground">New opportunities will appear here as they become available.</p>
        </div>
      ) : null}

      {!isLoading && !hasError && categories.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleCategories.map((category) => {
              const Icon = iconFor(category);
              const shownSpecializations = category.specializations.slice(0, 3);
              const remainingSpecializations = category.specializations.length - shownSpecializations.length;

              return (
                <article
                  key={category.value}
                  className="recent-job-card flex h-full min-h-[224px] flex-col p-5 motion-reduce:transition-none"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <a
                        href={functionalAreaJobsUrl(category.value)}
                        className="group inline-flex max-w-full items-start gap-1 rounded-sm text-[15px] font-semibold leading-5 text-foreground no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:text-base"
                      >
                        <span className="min-w-0 break-words group-hover:text-primary">{category.label}</span>
                        <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" aria-hidden="true" />
                      </a>
                      <p className="text-count mt-1 mb-0">{jobCountLabel(category.count)}</p>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-border pt-3">
                    {shownSpecializations.length > 0 ? (
                      <ul className="m-0 list-none space-y-2 p-0">
                        {shownSpecializations.map((specialization) => (
                          <li key={specialization.value}>
                            <a
                              href={functionalAreaJobsUrl(category.value, specialization.value)}
                              className="flex min-h-5 items-center justify-between gap-3 rounded-sm text-xs text-muted-foreground no-underline outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              aria-label={`View ${jobCountLabel(specialization.count)} in ${category.label}, ${specialization.label}`}
                            >
                              <span className="min-w-0 truncate">{specialization.label}</span>
                              <span className="text-count shrink-0">{specialization.count}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="m-0 text-xs text-muted-foreground">Explore available roles in this category.</p>
                    )}
                    {remainingSpecializations > 0 ? (
                      <p className="mb-0 mt-3 text-xs text-muted-foreground">+{remainingSpecializations} more specialization{remainingSpecializations === 1 ? "" : "s"}</p>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>

          {categories.length > INITIAL_CATEGORY_COUNT ? (
            <div className="mt-6 text-center">
              <Button type="button" variant="outline" onClick={() => setIsExpanded((expanded) => !expanded)}>
                {isExpanded ? "Show Less" : "View All Categories"}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
